import React, { useMemo } from 'react';
import { forEach } from 'lodash';

/* Components */
import CourtFormsLandingEmptyView from '~/src/components/CourtForms/CourtFormsLandingEmptyView';
import FederalSection from '~/src/components/CourtForms/FederalSection';
import FederalCategoriesPreviewSection from '~/src/components/CourtForms/FederalCategoriesPreviewSection';
import StateSection from '~/src/components/CourtForms/StateSection';
import { DrillDownPanelLink } from '~/src/components/DrillDownPanel';
import FederalFormsWithLocalitiesContainer from '~/src/containers/FederalFormsWithLocalitiesContainer';
import AdditionalRegionSections from '~/src/components/CourtForms/AdditionalRegionSections';

/* Hooks */
import useFeatureAccesses from '~/src/hooks/useFeatureAccesses';
import { useSubscription } from '~/src/entities/subscription';
import useGetTemplateMetadata from '~/src/hooks/useGetTemplateMetadata';
import {
  getAdditionalRegionForms,
  ADDITIONAL_REGION_FORMS_ROUTE_SEGMENT,
} from '~/src/utils/additionalRegionForms';
import {
  buildCourtFormUrl,
  CourtFormLevel,
} from '~/src/containers/RegionalCourtFormContainer/helpers';

export const stateCodeToStateName = {
  // USA
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',

  // Canada
  CA_BC: 'British Columbia',
};

const CourtFormsLandingContainer = ({ routeUrl = '/library/court-forms' }) => {
  const {
    isCourtFormsEnabled,
    isFederalCourtFormsEnabled,
    isFederalFormsCategoriesEnabled,
    doesFeatureAccessForThisStateCodeExist,
  } = useFeatureAccesses();

  const { data: subscriptionData } = useSubscription();
  const { data: templateMetadata } = useGetTemplateMetadata();
  const additionalRegions = getAdditionalRegionForms(
    subscriptionData?.additional_court_form_regions,
  );

  // Build state court form sections
  const stateCourtFormArray = useMemo(() => {
    const sections = [];
    forEach(stateCodeToStateName, (stateName, stateCode) => {
      const hasAccess = doesFeatureAccessForThisStateCodeExist(stateCode);
      if (hasAccess) {
        sections.push(
          <StateSection key={`${stateCode}_section`} stateName={stateName}>
            <DrillDownPanelLink
              key={`${stateCode}_form`}
              label={`${stateName} forms`}
              linkUrl={`${routeUrl}/${stateCode}/form-listing`}
            />
            <DrillDownPanelLink
              key={`${stateCode}_locality`}
              label={`${stateName} regional forms`}
              linkUrl={`${routeUrl}/${stateCode}/locality-listing`}
            />
          </StateSection>,
        );
      }
    });
    return sections;
  }, [doesFeatureAccessForThisStateCodeExist, routeUrl]);

  const additionalRegionSections = templateMetadata ? (
    <AdditionalRegionSections
      additionalRegions={additionalRegions}
      templateMetadata={templateMetadata}
      renderProvince={(region, province) => {
        const regionBaseUrl = `${routeUrl}${ADDITIONAL_REGION_FORMS_ROUTE_SEGMENT}/${region.region}`;
        return (
          <DrillDownPanelLink
            label={`${province} forms`}
            linkUrl={buildCourtFormUrl(
              regionBaseUrl,
              CourtFormLevel.SUB_FILTER,
              province,
            )}
          />
        );
      }}
    />
  ) : null;

  // Determine if user has ONLY federal forms (no state forms)
  const hasOnlyFederalForms =
    isFederalCourtFormsEnabled && stateCourtFormArray.length === 0;
  const hasFederalAndStateForms =
    isFederalCourtFormsEnabled && stateCourtFormArray.length > 0;

  if (!isCourtFormsEnabled && additionalRegions.length === 0) {
    return <CourtFormsLandingEmptyView />;
  }

  // Scenario: User has ONLY Federal Forms + feature flag enabled
  if (hasOnlyFederalForms && isFederalFormsCategoriesEnabled) {
    return (
      <div>
        {additionalRegionSections}
        <FederalFormsWithLocalitiesContainer
          routeUrl={routeUrl}
          showBreadcrumb={false}
        />
      </div>
    );
  }

  // Scenario: User has Federal + State Forms + feature flag enabled
  if (hasFederalAndStateForms && isFederalFormsCategoriesEnabled) {
    return (
      <div>
        {additionalRegionSections}
        <FederalCategoriesPreviewSection routeUrl={routeUrl} />
        {stateCourtFormArray}
      </div>
    );
  }

  // Default: Original behavior (feature flag OFF or only state forms)
  return (
    <div>
      {additionalRegionSections}
      {isFederalCourtFormsEnabled && (
        <FederalSection>
          <DrillDownPanelLink
            label="USCIS forms"
            linkUrl={`${routeUrl}/federal_USCIS`}
          />
        </FederalSection>
      )}

      {stateCourtFormArray}
    </div>
  );
};

export default CourtFormsLandingContainer;
